<template>
    <div :class="classList" class="elevation-3">
        <header class="app-progress-queue__header d-flex align-center justify-space-between pl-5 pr-2">
            <h3 class="app-progress-queue__title mt-1 clamp-1">{{ titleValue }}</h3>
            <div class="d-flex align-center justify-end">
                <app-button icon class="ml-2" @click="onToggleExpand">
                    <v-icon :class="expanded ? 'fa-rotate-90' : false" size="1.3rem" style="padding-bottom: 0.1rem">
                        {{ getIcon('expand') }}
                    </v-icon>
                </app-button>
                <app-button :icon="getIcon('close')" class="ml-2" @click="onClose" />
            </div>
        </header>
        <main class="app-progress-queue__main">
            <div
                v-for="item in itemsValue"
                :key="item.id"
                class="app-progress-queue__item d-flex align-start justify-start pa-2"
                :class="{
                    'app-progress-queue__item--success': !!item.success,
                    'app-progress-queue__item--error': !!item.error
                }"
            >
                <div class="app-progress-queue__item__icon ml-2 mr-1 mt-1 no-overflow">
                    <transition name="scroll-y-transition" mode="out-in">
                        <v-icon v-if="item.success" color="success">
                            {{ getIcon('success', 'fad') }}
                        </v-icon>
                        <v-icon v-else-if="item.error" color="error" small>
                            {{ getIcon('error', 'fas') }}
                        </v-icon>
                        <app-progress v-else :indeterminate="item.loading" />
                    </transition>
                </div>
                <div>
                    <p class="app-progress-queue__item__name mb-0 clamp-1">
                        {{ item.name }}
                    </p>
                    <transition name="fade" mode="out-in">
                        <p v-if="item.message" class="app-progress-queue__item__message mb-0 clamp-3 text--small">
                            {{ item.message }}
                        </p>
                    </transition>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import AppProgressQueueItem from '@/models/AppProgressQueueItem';

export const looksLikeAppProgressQueueItem = value => {
    if (value instanceof AppProgressQueueItem) {
        return true;
    }

    if (!value) {
        return false;
    }

    if (typeof value !== 'object') {
        return false;
    }

    return 'id' in value && 'name' in value;
};

export default {
    name: 'AppProgressQueue',
    props: {
        active: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: null
        },
        items: {
            type: Array,
            default: () => [],
            validator(value) {
                return value.every(item => looksLikeAppProgressQueueItem(item));
            }
        }
    },
    data() {
        return {
            isActive: false,
            titleValue: null,
            itemsValue: [],
            expanded: false
        };
    },
    computed: {
        progressQueue() {
            return this.$store.getters['app/progress_queue'];
        },
        classList() {
            let classList = ['app-progress-queue'];

            if (this.isActive) {
                classList.push('app-progress-queue--active');
            }

            if (this.expanded) {
                classList.push('app-progress-queue--expanded');
            }

            return classList;
        }
    },
    watch: {
        progressQueue: {
            handler() {
                this.isActive = this.progressQueue.active || false;
                this.titleValue = this.progressQueue.title || this.$t('progress');
                this.setItems(this.progressQueue.items);
            },
            deep: true
        },
        active() {
            this.isActive = this.active || false;
        },
        isActive(v) {
            this.expanded = v;
        },
        title() {
            this.titleValue = this.title || this.$t('progress');
        },
        items() {
            this.setItems(this.items);
        }
    },
    methods: {
        onToggleExpand() {
            this.expanded = !this.expanded;
        },
        onClose() {
            this.setAppProgressQueue(false);
        },
        setItems(items = []) {
            this.itemsValue = items
                .filter(item => looksLikeAppProgressQueueItem(item))
                .map(item => {
                    if (item instanceof AppProgressQueueItem) {
                        return item;
                    }

                    return new AppProgressQueueItem({ ...item });
                });
        }
    }
};
</script>

<style lang="scss" scoped>
$headerHeight: 3.4rem;

.app-progress-queue {
    position: fixed;
    bottom: 0;
    right: 1.2rem;
    background: var(--v-background-base);
    z-index: 4;
    border-radius: 1.6rem 1.6rem 0 0;
    width: 22rem;
    transform: translateY(100%);
    transition: transform 0.3s ease-in-out;

    &--active {
        transform: translateY(calc(100% - $headerHeight));
    }

    &--expanded {
        transform: translateY(0);
    }

    &__header {
        height: $headerHeight;
        border-bottom: 2px solid var(--v-primaryLight-base);
    }

    &__title {
        font-weight: normal;
        color: var(--v-primaryText-base);
        font-size: 1.3rem;
    }

    &__main {
        max-height: 20rem;
        overflow-y: scroll;
    }

    &__item {
        position: relative;
        color: var(--v-primaryText-base);

        &:not(:last-child) {
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }

        &--success {
            color: var(--v-success-base);
        }

        &--error {
            color: var(--v-error-base);
        }

        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            background: currentColor;
            z-index: 0;
            opacity: 0;
        }

        &:hover::before {
            opacity: 0.05;
        }

        &__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: 2rem;
        }

        &__name {
            filter: brightness(0.9);
        }

        &__message {
            filter: brightness(0.7);
        }
    }
}
</style>
